import {useEffect, useMemo, useState} from "react";
import {API, graphqlOperation} from "aws-amplify";
const adminMortgageTestForMaxesByMortgageID = /* GraphQL */`

    query AdminMortgageTestForMaxesByMortgageID(
        $mortgageID: ID!
        $sortDirection: ModelSortDirection
        $filter: ModelAdminMortgageTestForMaxFilterInput
        $limit: Int
        $nextToken: String
    ) {
        adminMortgageTestForMaxesByMortgageID(
            mortgageID: $mortgageID
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                lenders
                mortgageID
                status
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
                __typename
            }
            nextToken
            startedAt
            __typename
        }
    }
`;
const getAdminMortgageTestForMax = /* GraphQL */`

    query GetAdminMortgageTestForMax($id: ID!) {
        getAdminMortgageTestForMax(id: $id) {
            id
            mortgageID
            lenders
            figures
            status
            fastTests {
                items {
                    id
                    flowID
                    mortgageID
                    value
                    loan
                    maxTerm
                    maxLtv
                    lti
                    lenderCode
                    status
                    limitation
                    cashForLiving
                    runtime
                    url
                    bof
                    passed
                    createdAt
                    updatedAt
                    _version
                    _deleted
                    _lastChangedAt
                    __typename
                }
                nextToken
                startedAt
                __typename
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
            __typename
        }
    }
`;
const onUpdateAdminMortgageTestForMax = /* GraphQL */`

    subscription OnUpdateAdminMortgageTestForMax(
        $filter: ModelSubscriptionAdminMortgageTestForMaxFilterInput
    ) {
        onUpdateAdminMortgageTestForMax(filter: $filter) {
            id
            mortgageID
            lenders
            figures
            status
            fastTests {
                items {
                    id
                    flowID
                    mortgageID
                    value
                    loan
                    maxTerm
                    maxLtv
                    lti
                    bof
                    lenderCode
                    status
                    limitation
                    cashForLiving
                    runtime
                    url
                    passed
                    createdAt
                    updatedAt
                    _version
                    _deleted
                    _lastChangedAt
                    __typename
                }
                nextToken
                startedAt
                __typename
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
            __typename
        }
    }
`;
const onCreateAdminMortgageTestForMax = /* GraphQL */`

    subscription OnCreateAdminMortgageTestForMax(
        $filter: ModelSubscriptionAdminMortgageTestForMaxFilterInput
    ) {
        onCreateAdminMortgageTestForMax(filter: $filter) {
            id
            mortgageID
            lenders
            figures
            status
            fastTests {
                items {
                    id
                    flowID
                    mortgageID
                    value
                    loan
                    bof
                    maxTerm
                    maxLtv
                    lti
                    lenderCode
                    status
                    limitation
                    cashForLiving
                    runtime
                    url
                    passed
                    createdAt
                    updatedAt
                    _version
                    _deleted
                    _lastChangedAt
                    __typename
                }
                nextToken
                startedAt
                __typename
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
            __typename
        }
    }
`;
const onCreateAdminMortgageFastTest = /* GraphQL */`

    subscription OnCreateAdminMortgageFastTest(
        $filter: ModelSubscriptionAdminMortgageFastTestFilterInput
    ) {
        onCreateAdminMortgageFastTest(filter: $filter) {
            id
            flowID
            mortgageID
            value
            loan
            maxTerm
            maxLtv
            lti
            bof
            lenderCode
            status
            limitation
            cashForLiving
            runtime
            url
            passed
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
            __typename
        }
    }
`;
const onUpdateAdminMortgageFastTest = /* GraphQL */`

    subscription OnUpdateAdminMortgageFastTest(
        $filter: ModelSubscriptionAdminMortgageFastTestFilterInput
    ) {
        onUpdateAdminMortgageFastTest(filter: $filter) {
            id
            flowID
            mortgageID
            value
            loan
            maxTerm
            maxLtv
            lti
            bof
            lenderCode
            status
            limitation
            cashForLiving
            runtime
            url
            passed
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
            __typename
        }
    }
`;

export function useAdminMortgageTestForMax(mortgageID) {
    const [tests, setTests] = useState([])
    const [activeTestId, setActiveTestId] = useState(null)
    const [busy, setBusy] = useState(false)

    const loadFullTest = async (id) => {
        // Prevent duplicate loading if data already exists
        const active = tests.find(t => t.id === id)
        if (active?.fastTests?.items?.length > 0) {
            setActiveTestId(id)
            return
        }

        const {data} = await API.graphql(graphqlOperation(getAdminMortgageTestForMax, {id}))
        setTests(s => {
            let existing = s.find(t => t.id === id)
            if (existing) {
                return s.map(t => t.id === id ? data.getAdminMortgageTestForMax : t)
            }
            return [...s, data.getAdminMortgageTestForMax]
        })
        setActiveTestId(id)
    }

    // Combine the test history loading and subscription setup into a single effect
    useEffect(() => {
        if (!mortgageID) return

        let subscriptions = []

        loadTestHistory()

        // Set up subscriptions
        subscriptions = [
            API.graphql(graphqlOperation(onCreateAdminMortgageTestForMax)).subscribe({
                next: ({value}) => {
                    const newTest = value.data.onCreateAdminMortgageTestForMax
                    if (newTest.mortgageID !== mortgageID) return
                    setTests(prev => [newTest, ...prev])
                    setActiveTestId(newTest.id)
                }
            }),
            API.graphql(graphqlOperation(onUpdateAdminMortgageTestForMax)).subscribe({
                next: ({value}) => {
                    const update = value.data.onUpdateAdminMortgageTestForMax
                    if (update.mortgageID !== mortgageID) return
                    setTests(s => s.map(t => t.id === update.id ? {...t, ...update} : t))
                    if (update.status === 'PROCESSED') setBusy(false)
                }
            })
        ]

        // Cleanup subscriptions
        return () => {
            subscriptions.forEach(sub => sub.unsubscribe())
        }
    }, [mortgageID])

    // Handle fast test subscriptions separately
    useEffect(() => {
        if (!mortgageID) return

        const subscriptions = [
            API.graphql(graphqlOperation(onCreateAdminMortgageFastTest)).subscribe({
                next: ({value}) => {
                    const newChild = value.data.onCreateAdminMortgageFastTest
                    if (newChild.mortgageID !== mortgageID) return
                    const testId = newChild.flowID
                    setTests(s => s.map(t => t.id === testId ? {
                        ...t,
                        fastTests: {
                            items: [...(t.fastTests?.items || []), newChild]
                        }
                    } : t))
                }
            }),
            API.graphql(graphqlOperation(onUpdateAdminMortgageFastTest)).subscribe({
                next: ({value}) => {
                    const updatedChild = value.data.onUpdateAdminMortgageFastTest
                    if (updatedChild.mortgageID !== mortgageID) return
                    const testId = updatedChild.flowID
                    setTests(s => s.map(t => t.id === testId ? {
                        ...t,
                        fastTests: {
                            items: (t.fastTests?.items || []).map(ft =>
                                ft.id === updatedChild.id ? {...ft, ...updatedChild} : ft
                            )
                        }
                    } : t))
                }
            })
        ]

        return () => {
            subscriptions.forEach(sub => sub.unsubscribe())
        }
    }, [mortgageID])

    const activeTest = useMemo(() => {
        return tests.find(t => t.id === activeTestId)
    }, [tests, activeTestId])
    const loadTestHistory = async () => {
        if (!mortgageID) return
        API.graphql(graphqlOperation(adminMortgageTestForMaxesByMortgageID, {mortgageID}))
            .then(({data}) => {
                const sorted = data.adminMortgageTestForMaxesByMortgageID.items.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                setTests(sorted)
                if (sorted[0]) loadFullTest(sorted[0].id)
            })
    }
    const refreshTestHistory = async () => {
        setActiveTestId(null)
        setTests([])
        await loadTestHistory()
    }

    return {tests, activeTest, loadTest: loadFullTest, refreshTestHistory, busy, setBusy}
}
